import {
  BaseHandler,
  CategoryHandler,
  CollectionHandler,
  getAlgoliaConfig,
  PartnerProfileHandler,
  SearchFiltersHandler,
  SearchResultHandler,
  SequenceTypes,
  VersionHistoryHandler,
} from '@atlassian/mpac-services-client';

import type { BootstrapConfig } from '../application-config/types/BootstrapConfig';
import type { StatsigClientType } from '@atlassian/mpac-feature-flags';
import type {
  IOrchestrationConfig,
  IOrchestrationConfigRecord,
} from '@atlassian/mpac-services-client';

const getConfig = <T extends IOrchestrationConfigRecord>(config: IOrchestrationConfig<T>) => config;

/**
 * Orchestration configuration for DLA
 *
 * `config` accepts both a config and a function returning config. This can be used for returning
 *  configuration based on some feature flag or some custom logic.
 */
const config = (bootstrapConfig: BootstrapConfig, statsigClient: StatsigClientType) => {
  const algoliaConfig = getAlgoliaConfig(
    bootstrapConfig.appConfig.commonConfig.algoliaConfig,
    bootstrapConfig.featureFlags,
    true // all aether pages need v2 index
  );

  return getConfig({
    GetMarketplaceSearch: {
      config: () => ({
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new SearchResultHandler(algoliaConfig),
      }),
    },
    GetPartnerProfile: {
      config: () => ({
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new PartnerProfileHandler(algoliaConfig),
      }),
    },
    GetMarketplaceFilters: {
      config: () => ({
        sequence: SequenceTypes.CUSTOM_ONLY,
        handler: new SearchFiltersHandler(
          bootstrapConfig.stargateBaseUrl,
          bootstrapConfig.featureFlags,
          statsigClient
        ),
      }),
    },
    GetCollection: {
      middlewaresConfig: {
        cache: {
          primary: {
            ttlMs: 60_000,
            cacheKey: ({ variables }) => variables.slug,
          },
        },
      },
      config: () => ({
        sequence: SequenceTypes.PRIMARY_FIRST,
        handler: new CollectionHandler(algoliaConfig),
      }),
    },
    GetMarketplaceCategory: {
      middlewaresConfig: {
        cache: {
          primary: {
            ttlMs: 60_000, // todo: update TTL
            cacheKey: ({ variables }) => variables.slug,
          },
        },
      },
      config: {
        sequence: SequenceTypes.PRIMARY_FIRST,
        handler: new CategoryHandler(algoliaConfig),
      },
    },
    GetMarketplaceAppVersionHistory: {
      config: {
        sequence: SequenceTypes.PRIMARY_ONLY,
        handler: new VersionHistoryHandler(
          bootstrapConfig.mpacBaseUrl,
          bootstrapConfig.macBaseUrl,
          bootstrapConfig.wacBaseUrl
        ),
      },
    },
    WatchMarketplaceApp: {
      config: {
        sequence: SequenceTypes.PRIMARY_ONLY,
        handler: new BaseHandler(),
      },
    },
    UnwatchMarketplaceApp: {
      config: {
        sequence: SequenceTypes.PRIMARY_ONLY,
        handler: new BaseHandler(),
      },
    },
  });
};

export { config };
